/**
 * Country / international pricing context.
 *
 * Responsibilities:
 *   1. On first load, ask the backend's `/api/intl/geo` for the visitor's
 *      country (IP-based, best-effort).
 *   2. Persist the customer's chosen country in localStorage so navigation
 *      doesn't re-detect on every page.
 *   3. Expose:
 *        - `country` (the current chosen country, defaults to "IN")
 *        - `detectedCountry` (what geo-IP said — used for the "switch?" banner)
 *        - `pricing` (the full tiered pricing payload from the backend)
 *        - `setCountry(code)` — explicit user choice
 *
 *   4. When `detectedCountry != country` AND the customer hasn't dismissed
 *      the suggestion banner, the banner offers them a one-click switch.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_KEY = "tsp_country";
const BANNER_DISMISS_KEY = "tsp_country_banner_dismissed_for";

const CountryCtx = createContext(null);

const safeRead = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeWrite = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* ignore */ }
};

export const useCountry = () => useContext(CountryCtx) || {};

export const CountryProvider = ({ children }) => {
  const initialSaved = safeRead(COUNTRY_KEY) || "IN";
  const [country, setCountryState] = useState(initialSaved);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [fxRate, setFxRate] = useState(null);
  const [fxFetchedAt, setFxFetchedAt] = useState(null);

  // Fetch the live USD->INR rate once per session — used to display the
  // converted INR amount + actually charge intl customers via Razorpay.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/fx/usd-inr`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.rate) {
          setFxRate(Number(r.data.rate));
          setFxFetchedAt(r.data.fetched_at || null);
        }
      })
      .catch(() => { /* server falls back to its cached/default rate */ });
    return () => { alive = false; };
  }, []);

  // Run geo-IP detection exactly once per session.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/intl/geo`)
      .then((r) => {
        if (!alive) return;
        const detected = (r.data?.country || "").toUpperCase() || null;
        setDetectedCountry(detected);
        // First-time visitors get auto-set to their detected country
        // (only when no manual choice exists yet).
        if (detected && !safeRead(COUNTRY_KEY)) {
          setCountryState(detected);
          safeWrite(COUNTRY_KEY, detected);
        }
        // Suppress the banner if the customer previously dismissed it
        // FOR THIS DETECTED COUNTRY. Changing detected country re-shows it.
        const dismissedFor = safeRead(BANNER_DISMISS_KEY);
        if (detected && dismissedFor === detected) {
          setBannerDismissed(true);
        }
      })
      .catch(() => { /* ignore — keep saved country */ });
    return () => { alive = false; };
  }, []);

  // Refetch pricing whenever the chosen country changes.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/intl/pricing`, { params: { country } })
      .then((r) => alive && setPricing(r.data))
      .catch(() => alive && setPricing(null));
    return () => { alive = false; };
  }, [country]);

  const setCountry = useCallback((code) => {
    const next = (code || "IN").toUpperCase();
    setCountryState(next);
    safeWrite(COUNTRY_KEY, next);
    // User explicitly picked a country → the banner is no longer relevant.
    setBannerDismissed(true);
    if (detectedCountry) safeWrite(BANNER_DISMISS_KEY, detectedCountry);
  }, [detectedCountry]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    if (detectedCountry) safeWrite(BANNER_DISMISS_KEY, detectedCountry);
  }, [detectedCountry]);

  const isInternational = !!(pricing?.is_international);
  const currency = pricing?.currency || (isInternational ? "USD" : "INR");
  const currencySymbol = currency === "USD" ? "$" : "₹";

  // Helper to format a number for display — handles ₹/$ + locale grouping.
  const formatPrice = useCallback((value) => {
    if (value == null) return "";
    if (currency === "USD") return `$${Number(value).toFixed(2)}`;
    return `₹${Number(value).toLocaleString("en-IN")}`;
  }, [currency]);

  // Convert a USD value to INR using the latest fetched rate. Returns null
  // if the rate hasn't been fetched yet so callers can render a loading hint.
  const convertUsdToInr = useCallback((usd) => {
    if (usd == null || !fxRate) return null;
    return Math.round(Number(usd) * fxRate);
  }, [fxRate]);

  const formatInr = useCallback((value) => {
    if (value == null) return "";
    return `₹${Number(value).toLocaleString("en-IN")}`;
  }, []);

  // Banner is shown when:
  //  - we detected a country
  //  - it differs from the current chosen country
  //  - the user hasn't dismissed it for this detected country
  const showSwitchBanner =
    !!detectedCountry &&
    detectedCountry !== country &&
    !bannerDismissed;

  const value = useMemo(() => ({
    country,
    detectedCountry,
    pricing,
    isInternational,
    currency,
    currencySymbol,
    formatPrice,
    formatInr,
    convertUsdToInr,
    fxRate,
    fxFetchedAt,
    setCountry,
    showSwitchBanner,
    dismissBanner,
  }), [country, detectedCountry, pricing, isInternational, currency, currencySymbol, formatPrice, formatInr, convertUsdToInr, fxRate, fxFetchedAt, setCountry, showSwitchBanner, dismissBanner]);

  return <CountryCtx.Provider value={value}>{children}</CountryCtx.Provider>;
};
